import type {
  TenantAnalyticsAdapter,
  TenantAnalyticsConsentSnapshot,
} from '@kit/analytics/tenant';
import {
  type MetaPixelPublicConfig,
  type SnapPixelPublicConfig,
  type TikTokPixelPublicConfig,
  createMetaPixelAdapter,
  createSnapPixelAdapter,
  createTikTokPixelAdapter,
  parseMetaPixelPublicConfig,
  parseSnapPixelPublicConfig,
  parseTikTokPixelPublicConfig,
} from '@kit/analytics/tenant/pixels';

import type {
  TenantAnalyticsClientConfiguration,
  TenantGoogleScriptDescriptor,
} from './tenant-google-browser';

/**
 * Advertising pixels differ from the Google transports in one deliberate way:
 * consent gates the LOAD, not just delivery. No pixel script, queue, or
 * cookie exists in the page until the visitor grants marketing consent —
 * there is no pixel equivalent of Google's Consent Mode "denied" telemetry
 * worth loading for.
 *
 * The vendor globals (fbq / ttq / snaptr) are page-wide singletons, which is
 * safe here: tenants live on separate origins, and the per-(tenant, provider,
 * environment) uniqueness of the settings table means at most one pixel per
 * vendor exists on a page.
 */
export type TenantPixelTransport =
  | {
      readonly provider: 'meta';
      readonly publicConfig: MetaPixelPublicConfig;
      readonly key: string;
    }
  | {
      readonly provider: 'tiktok';
      readonly publicConfig: TikTokPixelPublicConfig;
      readonly key: string;
    }
  | {
      readonly provider: 'snapchat';
      readonly publicConfig: SnapPixelPublicConfig;
      readonly key: string;
    };

const PIXEL_SCRIPT_SOURCES = {
  meta: 'https://connect.facebook.net/en_US/fbevents.js',
  tiktok: 'https://analytics.tiktok.com/i18n/pixel/events.js',
  snapchat: 'https://sc-static.net/scevent.min.js',
} as const;

/** Shims installed + vendor init queued — never undone (scripts can't unload). */
const installedPixelKeys = new Set<string>();
const installedPixelKeyByProvider = new Map<
  TenantPixelTransport['provider'],
  string
>();
/** Consent-granted state per transport key; add/removed as consent changes. */
const activatedPixelKeys = new Set<string>();
const readyPixelKeys = new Set<string>();
const activationListeners = new Set<() => void>();

export function subscribeToTenantPixelActivation(onStoreChange: () => void) {
  activationListeners.add(onStoreChange);

  return () => {
    activationListeners.delete(onStoreChange);
  };
}

export function areTenantPixelTransportsActivated(
  transports: readonly TenantPixelTransport[],
) {
  return (
    transports.length > 0 &&
    transports.every((transport) => activatedPixelKeys.has(transport.key))
  );
}

export function areTenantPixelTransportsReady(
  transports: readonly TenantPixelTransport[],
) {
  return (
    transports.length > 0 &&
    transports.every((transport) => readyPixelKeys.has(transport.key))
  );
}

export function setTenantPixelTransportReady(key: string, ready: boolean) {
  const changed = ready ? !readyPixelKeys.has(key) : readyPixelKeys.has(key);

  if (!changed) {
    return;
  }

  if (ready) {
    readyPixelKeys.add(key);
  } else {
    readyPixelKeys.delete(key);
  }

  notifyPixelActivationChange();
}

/**
 * Pixel SDK globals cannot all replace an initialized destination safely.
 * Suppress the new runtime until a hard reload clears the old vendor globals.
 */
export function tenantPixelTransportsRequireHardReload(
  transports: readonly TenantPixelTransport[],
) {
  const currentProviders = new Set(
    transports.map((transport) => transport.provider),
  );

  const replacesInstalledProvider = transports.some((transport) => {
    const installedKey = installedPixelKeyByProvider.get(transport.provider);

    return installedKey !== undefined && installedKey !== transport.key;
  });

  const removesInstalledProvider = Array.from(
    installedPixelKeyByProvider.keys(),
  ).some((provider) => !currentProviders.has(provider));

  return replacesInstalledProvider || removesInstalledProvider;
}

function notifyPixelActivationChange() {
  activationListeners.forEach((listener) => listener());
}

export function selectTenantPixelTransports(
  tenantId: string,
  configuration: TenantAnalyticsClientConfiguration,
): readonly TenantPixelTransport[] {
  const environment = configuration.environment;

  if (!environment || tenantId.trim().length === 0) {
    return [];
  }

  const transports: TenantPixelTransport[] = [];

  for (const integration of configuration.integrations) {
    if (
      integration.environment !== environment ||
      !Number.isInteger(integration.configVersion) ||
      integration.configVersion < 1
    ) {
      continue;
    }

    const key = [
      tenantId.trim(),
      environment,
      integration.provider,
      integration.configVersion,
    ].join(':');

    if (integration.provider === 'meta') {
      const publicConfig = parseMetaPixelPublicConfig(integration.publicConfig);

      if (publicConfig) {
        transports.push({ provider: 'meta', publicConfig, key });
      }
    } else if (integration.provider === 'tiktok') {
      const publicConfig = parseTikTokPixelPublicConfig(
        integration.publicConfig,
      );

      if (publicConfig) {
        transports.push({ provider: 'tiktok', publicConfig, key });
      }
    } else if (integration.provider === 'snapchat') {
      const publicConfig = parseSnapPixelPublicConfig(integration.publicConfig);

      if (publicConfig) {
        transports.push({ provider: 'snapchat', publicConfig, key });
      }
    }
  }

  return Object.freeze(transports);
}

export function hasRequiredTenantPixelConsent(
  consent: TenantAnalyticsConsentSnapshot,
) {
  return (
    consent.necessary === true &&
    consent.analytics === true &&
    consent.advertising === true
  );
}

/**
 * Installs the vendor command queues and init commands synchronously — the
 * same shim pattern the official snippets use, implemented in TypeScript so
 * no inline HTML is injected. Must run before the vendor script tags render.
 * Idempotent per transport key. Returns true when the pixels may load.
 */
export function activateTenantPixelTransports(
  transports: readonly TenantPixelTransport[],
  consent: TenantAnalyticsConsentSnapshot,
) {
  if (!hasRequiredTenantPixelConsent(consent)) {
    return false;
  }

  let changed = false;

  for (const transport of transports) {
    if (activatedPixelKeys.has(transport.key)) {
      continue;
    }

    if (!installedPixelKeys.has(transport.key)) {
      switch (transport.provider) {
        case 'meta':
          installMetaQueue();
          callMeta('consent', 'grant');
          callMeta('init', transport.publicConfig.pixelId);
          break;
        case 'tiktok':
          installTikTokQueue(transport.publicConfig.pixelCode);
          break;
        case 'snapchat':
          installSnapQueue();
          callSnap('init', transport.publicConfig.pixelId);
          break;
      }

      installedPixelKeys.add(transport.key);
      installedPixelKeyByProvider.set(transport.provider, transport.key);
    } else if (transport.provider === 'meta') {
      // Re-grant after a mid-session withdrawal.
      callMeta('consent', 'grant');
    } else if (transport.provider === 'tiktok') {
      getTikTok()?.grantConsent?.();
    }

    activatedPixelKeys.add(transport.key);
    changed = true;
  }

  if (changed) {
    notifyPixelActivationChange();
  }

  return true;
}

/**
 * Best-effort mid-session withdrawal: vendors with a consent API are told to
 * stop; for the rest the runtime's per-adapter consent gate already blocks
 * every further delivery. Loaded scripts cannot be unloaded.
 */
export function revokeTenantPixelTransports(
  transports: readonly TenantPixelTransport[],
) {
  let changed = false;

  for (const transport of transports) {
    if (!activatedPixelKeys.has(transport.key)) {
      continue;
    }

    if (transport.provider === 'meta') {
      callMeta('consent', 'revoke');
    } else if (transport.provider === 'tiktok') {
      getTikTok()?.revokeConsent?.();
    }

    activatedPixelKeys.delete(transport.key);
    readyPixelKeys.delete(transport.key);
    changed = true;
  }

  if (changed) {
    notifyPixelActivationChange();
  }
}

export function getConsentGatedTenantPixelScripts(
  transports: readonly TenantPixelTransport[],
  consent: TenantAnalyticsConsentSnapshot,
  isActivated: boolean,
): readonly (TenantGoogleScriptDescriptor & { readonly key: string })[] {
  if (!isActivated || !hasRequiredTenantPixelConsent(consent)) {
    return [];
  }

  return transports
    .filter((transport) => activatedPixelKeys.has(transport.key))
    .map((transport) => ({
      key: transport.key,
      id: `tenant-pixel-${transport.provider}-${hashTransportKey(transport.key)}`,
      src:
        transport.provider === 'tiktok'
          ? `${PIXEL_SCRIPT_SOURCES.tiktok}?sdkid=${encodeURIComponent(transport.publicConfig.pixelCode)}&lib=ttq`
          : PIXEL_SCRIPT_SOURCES[transport.provider],
    }));
}

export function createTenantPixelBrowserAdapters(
  transports: readonly TenantPixelTransport[],
): readonly TenantAnalyticsAdapter[] {
  return transports.map((transport) => {
    switch (transport.provider) {
      case 'meta':
        return createMetaPixelAdapter({
          publicConfig: transport.publicConfig,
          id: `tenant:${transport.key}`,
          send: (eventName, parameters, options) => {
            callMeta(
              'trackSingle',
              transport.publicConfig.pixelId,
              eventName,
              parameters,
              options,
            );
          },
        });
      case 'tiktok':
        return createTikTokPixelAdapter({
          publicConfig: transport.publicConfig,
          id: `tenant:${transport.key}`,
          sink: {
            page: () => {
              getTikTok()?.page();
            },
            track: (eventName, parameters, options) => {
              getTikTok()?.track(eventName, parameters, options);
            },
          },
        });
      case 'snapchat':
        return createSnapPixelAdapter({
          publicConfig: transport.publicConfig,
          id: `tenant:${transport.key}`,
          send: (eventName, parameters) => {
            callSnap('track', eventName, parameters);
          },
        });
    }
  });
}

/* ------------------------------------------------------------------ */
/* Vendor queue shims — TypeScript ports of the official bootstraps.   */
/* Each creates a callable that queues commands until the vendor       */
/* script arrives and replays them.                                    */
/* ------------------------------------------------------------------ */

interface QueuedCallable {
  (...args: unknown[]): void;
  queue?: unknown[][];
  callMethod?: (...args: unknown[]) => void;
  push?: unknown;
  loaded?: boolean;
  version?: string;
}

function getBrowserWindow() {
  return window as unknown as Record<string, unknown>;
}

function installMetaQueue() {
  const browserWindow = getBrowserWindow();

  if (browserWindow.fbq) {
    return;
  }

  const fbq: QueuedCallable = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue?.push(args);
    }
  };

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  browserWindow.fbq = fbq;
  browserWindow._fbq = fbq;
}

function callMeta(...args: unknown[]) {
  const fbq = getBrowserWindow().fbq as QueuedCallable | undefined;
  fbq?.(...args);
}

interface TikTokQueue {
  page(): void;
  track(eventName: string, parameters: unknown, options: unknown): void;
  grantConsent?: () => void;
  revokeConsent?: () => void;
  [key: string]: unknown;
}

const TIKTOK_METHODS = [
  'page',
  'track',
  'identify',
  'instances',
  'debug',
  'on',
  'off',
  'once',
  'ready',
  'alias',
  'group',
  'enableCookie',
  'disableCookie',
  'holdConsent',
  'revokeConsent',
  'grantConsent',
] as const;

function installTikTokQueue(pixelCode: string) {
  const browserWindow = getBrowserWindow();

  if (browserWindow.ttq) {
    return;
  }

  const ttq = [] as unknown as TikTokQueue & unknown[];

  for (const method of TIKTOK_METHODS) {
    (ttq as Record<string, unknown>)[method] = (...args: unknown[]) => {
      ttq.push([method, ...args]);
    };
  }

  // Mirrors ttq.load bookkeeping so the vendor script adopts the queue.
  const instances: Record<string, unknown> = {};
  (ttq as Record<string, unknown>)._i = instances;
  instances[pixelCode] = [];
  (ttq as Record<string, unknown>)._t = { [pixelCode]: Date.now() };
  (ttq as Record<string, unknown>)._o = { [pixelCode]: {} };

  browserWindow.TiktokAnalyticsObject = 'ttq';
  browserWindow.ttq = ttq;
}

function getTikTok() {
  return getBrowserWindow().ttq as TikTokQueue | undefined;
}

function installSnapQueue() {
  const browserWindow = getBrowserWindow();

  if (browserWindow.snaptr) {
    return;
  }

  const snaptr: QueuedCallable = function (...args: unknown[]) {
    if (snaptr.callMethod) {
      snaptr.callMethod(...args);
    } else {
      snaptr.queue?.push(args);
    }
  };

  snaptr.queue = [];
  browserWindow.snaptr = snaptr;
}

function callSnap(...args: unknown[]) {
  const snaptr = getBrowserWindow().snaptr as QueuedCallable | undefined;
  snaptr?.(...args);
}

function hashTransportKey(key: string) {
  let hash = 2_166_136_261;

  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }

  return (hash >>> 0).toString(36);
}
